import { ONE_MINUTE_IN_MILLISECONDS } from 'consts/consts';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useLocalStorageOrQueryParamState } from 'utils/util/hooks';
import {
  AuthUser,
  Features,
  Me,
  Organization,
  Person,
  ReduxState,
} from '../../types';
import { unifiedIntercomIsEnabled } from '../../utils/util/features';
import Intercom, { IntercomAPI } from '../../vendor/react-intercom/src';

// Set by useUnifiedIntercom while the support chat must stay closed (e.g. while
// impersonating, when the messenger is not booted at all); every "contact
// support" entry point goes through showSupportChat so the person gets an
// explanation instead of nothing happening.
let supportChatDisabledMessage: string | null = null;

export const setSupportChatDisabledMessage = (message: string | null): void => {
  supportChatDisabledMessage = message;
};

export const isSupportChatDisabled = (): boolean =>
  supportChatDisabledMessage !== null;

export const showSupportChat = (): void => {
  if (supportChatDisabledMessage) {
    toast.warn(supportChatDisabledMessage);
    return;
  }

  IntercomAPI('show');
};

// Append `?force_unified_intercom=true` (or `=false`) to any app url to
// override the org flag for this browser for a while, e.g. to try the unified
// messenger on an org that is not switched yet. The backend honours the same
// parameter is only read by the frontend: the backend always issues a token
// for the caller's own identity.
export const UNIFIED_INTERCOM_FORCE_PARAM = 'force_unified_intercom';
const UNIFIED_INTERCOM_FORCE_TTL_MS = 30 * ONE_MINUTE_IN_MILLISECONDS;

const identity = (value: string | null): string | undefined =>
  value ?? undefined;

export const useUnifiedIntercomEnabled = (): boolean => {
  const features = useSelector<ReduxState, Features>((state) => state.features);

  const storedValue = useLocalStorageOrQueryParamState<string | undefined>(
    UNIFIED_INTERCOM_FORCE_PARAM,
    undefined,
    identity,
    { ttlInMs: UNIFIED_INTERCOM_FORCE_TTL_MS }
  );

  // The stored value only lands after an effect; read the url directly too
  // so a forced page load never boots the legacy messenger first.
  const queryValue = useMemo(
    () =>
      new URLSearchParams(window.location.search).get(
        UNIFIED_INTERCOM_FORCE_PARAM
      ),
    []
  );
  const forcedValue = queryValue ?? storedValue;

  if (forcedValue === 'true') {
    return true;
  }

  if (forcedValue === 'false') {
    return false;
  }

  return unifiedIntercomIsEnabled(features);
};

const UNIFIED_INTERCOM_API_BASE = 'https://api-iam.intercom.io';
// Refresh the JWT this long before it expires, and never more often than this.
const UNIFIED_INTERCOM_JWT_REFRESH_MARGIN_MS = 5 * 60 * 1000;
const UNIFIED_INTERCOM_JWT_MIN_REFRESH_MS = 60 * 1000;

type UnifiedIntercomConfig = {
  unified_intercom_jwt: string;
  app_id: string;
  messenger_environment: string;
  expires_at: number;
};

type UnifiedIntercomState = {
  // whether the unified messenger takes over from the legacy one
  enabled: boolean;
  // whether it should actually boot right now (enabled, authenticated, not
  // impersonating)
  active: boolean;
  // undefined while loading, null when the JWT could not be fetched
  config: UnifiedIntercomConfig | null | undefined;
};

// Fetches the server-signed JWT for the unified Intercom workspace, keeps it
// fresh, and keeps the support chat entry points informed while the messenger
// must stay closed (impersonation).
export const useUnifiedIntercom = (): UnifiedIntercomState => {
  const { formatMessage } = useIntl();
  const enabled = useUnifiedIntercomEnabled();
  const authUser = useSelector<ReduxState, AuthUser | undefined>(
    (state) => state.authUser
  );
  const currentOrganizationId = useSelector<ReduxState, number | undefined>(
    (state) => state.currentOrganization?.id
  );
  const currentProxyPerson = useSelector<ReduxState, Person | null>(
    (state) => state.currentProxyPerson
  );
  const isImpersonating = !!currentProxyPerson;

  // The unified messenger never boots while impersonating: the JWT would
  // identify the proxied person and attach the admin's conversations to them.
  const active =
    enabled && !!authUser && !!currentOrganizationId && !isImpersonating;

  const [config, setConfig] = useState<
    UnifiedIntercomConfig | null | undefined
  >(undefined);

  const fetchConfig = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'unified-intercom-jwt',
      { organization: currentOrganizationId },
      (data) => {
        // data is null on any request failure
        setConfig(data?.unified_intercom_jwt ? data : null);
      }
    );
  }, [currentOrganizationId]);

  useEffect(() => {
    // Fetched once per page load and deliberately never cached in local
    // storage: the JWT is short-lived and identifies the person to Intercom.
    if (!active) {
      setConfig(undefined);
      return;
    }

    fetchConfig();
  }, [active, fetchConfig]);

  useEffect(() => {
    // Refresh the JWT before it expires so later messenger updates (locale,
    // launcher visibility) keep carrying a valid token.
    if (!active || !config?.expires_at) {
      return;
    }

    const refreshInMs = Math.max(
      config.expires_at * 1000 -
        Date.now() -
        UNIFIED_INTERCOM_JWT_REFRESH_MARGIN_MS,
      UNIFIED_INTERCOM_JWT_MIN_REFRESH_MS
    );
    const timer = setTimeout(fetchConfig, refreshInMs);

    return () => clearTimeout(timer);
  }, [active, config, fetchConfig]);

  const currentProxyPersonName = currentProxyPerson?.full_name;

  useEffect(() => {
    if (enabled && isImpersonating) {
      setSupportChatDisabledMessage(
        formatMessage(
          {
            id: 'app.views.layout.header.support_chat_disabled_while_impersonating',
            defaultMessage:
              'Support chat is off while you are viewing as {name}, so conversations stay on the right person.',
          },
          { name: currentProxyPersonName }
        )
      );
    } else {
      setSupportChatDisabledMessage(null);
    }

    return () => setSupportChatDisabledMessage(null);
  }, [enabled, isImpersonating, currentProxyPersonName, formatMessage]);

  return useMemo(
    () => ({ enabled, active, config }),
    [enabled, active, config]
  );
};

interface Props {
  hideDefaultLauncher: boolean;
  languageOverride?: string;
  isAdmin: boolean;
}

// Boots the messenger against the unified Intercom workspace, identified by
// the server-signed JWT. Renders nothing while off, loading or impersonating.
const UnifiedIntercom: FC<Props> = ({
  hideDefaultLauncher,
  languageOverride,
  isAdmin,
}) => {
  const { active, config } = useUnifiedIntercom();
  const currentOrganization = useSelector<ReduxState, Organization | undefined>(
    (state) => state.currentOrganization
  );
  const underlyingAuthMe = useSelector<ReduxState, Me | undefined>(
    // @ts-expect-error
    (state) => state.underlyingAuthMe
  );

  return useMemo(() => {
    if (!active || typeof config === 'undefined') {
      // off, impersonating, or the JWT is still loading
      return null;
    }

    if (!config) {
      console.error(
        'Unified Intercom JWT is missing for user ' +
          underlyingAuthMe?.email +
          ' (ID: ' +
          underlyingAuthMe?.user_id +
          ')'
      );
      return null;
    }

    return (
      <Intercom
        appID={config.app_id}
        api_base={UNIFIED_INTERCOM_API_BASE}
        messenger_environment={config.messenger_environment}
        intercom_user_jwt={config.unified_intercom_jwt}
        hide_default_launcher={hideDefaultLauncher}
        language_override={languageOverride}
        is_admin={isAdmin}
        company={
          currentOrganization?.id
            ? {
                company_id: currentOrganization.id,
                name: currentOrganization.name,
              }
            : undefined
        }
      />
    );
  }, [
    active,
    config,
    hideDefaultLauncher,
    languageOverride,
    isAdmin,
    currentOrganization?.id,
    currentOrganization?.name,
    underlyingAuthMe?.email,
    underlyingAuthMe?.user_id,
  ]);
};

export default UnifiedIntercom;
